import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  Box,
  Card,
  CardContent,
  Typography,
  useTheme,
  CircularProgress,
  Button,
  useMediaQuery,
  Tooltip as MuiTooltip,
  Skeleton,
  Paper,
  Avatar,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { tokens } from "../../../theme";
import {
  InfoOutlined,
  TableChart,
  PieChart as PieChartIcon,
  Download,
  PendingActions,
  CheckCircle,
  AssignmentTurnedIn,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

const ApprovalPieChart = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const token = localStorage.getItem("token");
  const cmpid = localStorage.getItem("cmpid");
  const userRole = localStorage.getItem("Role");

  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isUsingDummyData, setIsUsingDummyData] = useState(false);
  const [viewMode, setViewMode] = useState("pie");
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [isModuleActive, setIsModuleActive] = useState(false);
  const navigate = useNavigate();

  const [stats, setStats] = useState({
    totalApprovals: 0,
    primaryCount: 0,
    secondaryCount: 0,
    finalCount: 0,
    highestLevel: { name: "", value: 0 },
    lowestLevel: { name: "", value: 0 },
  });

  const CONFIG = {
    colors: {
      primary: colors.blueAccent[500],
      secondary: colors.greenAccent[500],
      final: colors.redAccent[500],
      hoverPrimary: colors.blueAccent[400],
      hoverSecondary: colors.greenAccent[400],
      hoverFinal: colors.redAccent[400],
      cardBg: theme.palette.background.default,
      detailsBg:
        theme.palette.mode === "dark"
          ? colors.primary[900]
          : colors.primary[100],
      textPrimary: theme.palette.text.primary,
      textSecondary: theme.palette.text.secondary,
      error: theme.palette.error.main,
      chipBg:
        theme.palette.mode === "dark"
          ? colors.blueAccent[900]
          : colors.blueAccent[100],
      warningBg:
        theme.palette.mode === "dark"
          ? colors.blueAccent[900]
          : colors.blueAccent[100],
      warningText:
        theme.palette.mode === "dark"
          ? colors.primary[100]
          : colors.blueAccent[900],
    },
    dataKeys: {
      primary: "Primary Approval",
      secondary: "Secondary Approval",
      final: "Final Approval",
    },
    messages: {
      title: "Approval Dashboard",
      detailsTitle: "Pending Approval Details",
      demoLabel: "Showing Demo Data",
      moduleInactive: "Approval Module is currently inactive",
      enableModule: "Enable Approval Module",
      noData: "No approval data available",
    },
    chart: {
      outerRadius: isMobile ? 90 : 110,
      innerRadius: isMobile ? 50 : 60,
    },
  };

  const generateSampleData = () => {
    return [
      { name: CONFIG.dataKeys.primary, value: 12 },
      { name: CONFIG.dataKeys.secondary, value: 8 },
      { name: CONFIG.dataKeys.final, value: 5 },
    ];
  };

  const calculateStats = (data) => {
    if (!data || data.length === 0) return {};

    const primaryCount =
      data.find((item) => item.name === CONFIG.dataKeys.primary)?.value || 0;
    const secondaryCount =
      data.find((item) => item.name === CONFIG.dataKeys.secondary)?.value || 0;
    const finalCount =
      data.find((item) => item.name === CONFIG.dataKeys.final)?.value || 0;
    const totalApprovals = primaryCount + secondaryCount + finalCount;

    const highestLevel = data.reduce(
      (max, item) =>
        item.value > max.value ? { name: item.name, value: item.value } : max,
      { name: "", value: -Infinity }
    );

    const lowestLevel = data.reduce(
      (min, item) =>
        item.value < min.value ? { name: item.name, value: item.value } : min,
      { name: "", value: Infinity }
    );

    return {
      totalApprovals,
      primaryCount,
      secondaryCount,
      finalCount,
      highestLevel,
      lowestLevel,
      primaryPercentage:
        totalApprovals > 0 ? (primaryCount / totalApprovals) * 100 : 0,
      secondaryPercentage:
        totalApprovals > 0 ? (secondaryCount / totalApprovals) * 100 : 0,
      finalPercentage:
        totalApprovals > 0 ? (finalCount / totalApprovals) * 100 : 0,
    };
  };

  const loadDummyData = () => {
    setIsUsingDummyData(true);
    const dummyData = generateSampleData();
    setChartData(dummyData);
    setStats(calculateStats(dummyData));
    setLoading(false);
  };

  const fetchEnabledModules = async () => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/module/enabledModuleslist`,
        { cmpid },
        { headers: { Authorization: token } }
      );

      const modulesArray = Object.values(response.data);

      const approvalModule = modulesArray.find(
        (module) => module.module_Name === "Approval Module"
      );

      const moduleStatus = approvalModule?.status === 1;
      setIsModuleActive(moduleStatus);

      if (moduleStatus) {
        await fetchRealData();
      } else {
        loadDummyData();
      }
    } catch (err) {
      loadDummyData();
    }
  };

  const fetchRealData = async () => {
    try {
      setLoading(true);
      setIsUsingDummyData(false);

      let counts = {
        finalCount: 0,
        primaryCount: 0,
        secondaryCount: 0,
      };

      const approvalResponse = await axios.post(
        `${process.env.REACT_APP_API_URL}/approvalModule/vchApprovedCount`,
        { cmpid },
        { headers: { Authorization: token } }
      );
      counts = approvalResponse.data[0] || counts;
// console.log(counts)
      const fullData = [
        { name: CONFIG.dataKeys.primary, value: counts.primaryCount },
        { name: CONFIG.dataKeys.secondary, value: counts.secondaryCount },
        { name: CONFIG.dataKeys.final, value: counts.finalCount },
      ];

      setChartData(fullData);
      setStats(calculateStats(fullData));
      // console.log(calculateStats(fullData))
    } catch (err) {
      loadDummyData();
    } finally {
      setLoading(false);
    }
  };

  const toggleViewMode = () => {
    setViewMode(viewMode === "pie" ? "table" : "pie");
  };

  const exportToCSV = () => {
    const headers = ["Approval Level", "Pending Count", "Percentage of Total"];

    const rows = chartData.map((item) => {
      const percentage = (item.value / stats.totalApprovals) * 100;
      return [
        item.name || "N/A",
        item.value || 0,
        stats.totalApprovals > 0 ? percentage.toFixed(1) + "%" : "0%",
      ];
    });

    if (stats.totalApprovals > 0) {
      rows.push(["Total", stats.totalApprovals, "100%"]);
    }

    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `approval_data_${new Date().toISOString().slice(0, 10)}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Handle click on approval level
  const handleApprovalLevelClick = (approvalLevel) => {
    if (userRole === "admin") {
      navigate("/Infinite/AdminList");
      return;
    } else {
      switch (approvalLevel) {
        case CONFIG.dataKeys.primary:
          navigate("/Infinite/PrimaryApprov");
          break;
        case CONFIG.dataKeys.secondary:
          navigate("/Infinite/SecondaryApprov");
          break;
        case CONFIG.dataKeys.final:
          navigate("/Infinite/FinalApprov");
          break;
        default:
          break;
      }
    }
  };

  // Handle click on pie chart segment
  const handlePieClick = (data, index) => {
    if (data && data.name) {
      handleApprovalLevelClick(data.name);
    }
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <Card
          sx={{
            backgroundColor: CONFIG.colors.cardBg,
            boxShadow: theme.shadows[3],
            borderRadius: 2,
            p: 1.5,
            minWidth: 120,
          }}
        >
          <Typography
            variant="subtitle2"
            fontWeight={600}
            gutterBottom
            color={CONFIG.colors.textPrimary}
          >
            {payload[0].name}
          </Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <Box
              sx={{
                width: 12,
                height: 12,
                backgroundColor: payload[0].payload.fill,
                borderRadius: "2px",
              }}
            />
            <Typography variant="body2" color={CONFIG.colors.textSecondary}>
              {payload[0].value} pending
            </Typography>
          </Box>
          {isUsingDummyData && (
            <Typography
              variant="caption"
              color="error"
              sx={{ display: "block", mt: 0.5 }}
            >
              (Demo Data - Module Inactive)
            </Typography>
          )}
        </Card>
      );
    }
    return null;
  };

  const renderCustomizedLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    percent,
    index,
  }) => {
    const radius = innerRadius + (outerRadius - innerRadius) * 0.7;
    const x = cx + radius * Math.cos((-midAngle * Math.PI) / 180);
    const y = cy + radius * Math.sin((-midAngle * Math.PI) / 180);

    return (
      <text
        x={x}
        y={y}
        fill={CONFIG.colors.textPrimary}
        textAnchor="middle"
        dominantBaseline="central"
        style={{
          fontSize: isMobile ? 10 : 12,
          fontWeight: 600,
          opacity: hoveredIndex !== null ? 0.7 : 1,
          transition: "all 0.3s ease",
        }}
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  const getSegmentColor = (index, isHovered) => {
    const colors = {
      0: isHovered ? CONFIG.colors.hoverPrimary : CONFIG.colors.primary,
      1: isHovered ? CONFIG.colors.hoverSecondary : CONFIG.colors.secondary,
      2: isHovered ? CONFIG.colors.hoverFinal : CONFIG.colors.final,
    };
    return colors[index] || CONFIG.colors.primary;
  };

  const handleEnableModule = () => {
    navigate("/Infinite/TallyCompanies");
  };

  useEffect(() => {
    fetchEnabledModules();
  }, []);

  return (
    <Card
      sx={{
        height: "100%",
        minHeight: 450,
        backgroundColor: theme.palette.background.paper,
        boxShadow: theme.shadows[2],
        borderRadius: "12px",
        overflow: "hidden",
        border: `1px solid ${theme.palette.divider}`,
        display: "flex",
        flexDirection: "column",
        transition: "all 0.3s ease",
        "&:hover": {
          boxShadow: theme.shadows[6],
        },
      }}
    >
      <CardContent
        sx={{
          p: isMobile ? 2 : 3,
          flex: 1,
          display: "flex",
          flexDirection: "column",
          gap: 1,
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={2}
        >
          <Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: CONFIG.colors.textPrimary,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Avatar
                sx={{
                  bgcolor: colors.blueAccent[500],
                  width: 32,
                  height: 32,
                  boxShadow: theme.shadows[2],
                }}
              >
                <PendingActions fontSize="small" />
              </Avatar>
              Approval Dashboard
              <MuiTooltip title="Overview of pending approvals at different levels">
                <InfoOutlined
                  sx={{
                    fontSize: 18,
                    color: CONFIG.colors.textSecondary,
                    cursor: "pointer",
                  }}
                />
              </MuiTooltip>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Pending approvals by level
            </Typography>
          </Box>

          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <Button
              size="small"
              onClick={toggleViewMode}
              startIcon={viewMode === "pie" ? <TableChart /> : <PieChartIcon />}
              sx={{
                textTransform: "none",
                color: CONFIG.colors.textSecondary,
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              {viewMode === "pie" ? "Table" : "Pie"}
            </Button>
            <Button
              size="small"
              onClick={exportToCSV}
              startIcon={<Download />}
              disabled={loading}
              sx={{
                textTransform: "none",
                color: CONFIG.colors.textSecondary,
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              Export
            </Button>
          </Box>
        </Box>

        {!isModuleActive && !loading && (
          <Box
            sx={{
              backgroundColor: CONFIG.colors.warningBg,
              p: 2,
              borderRadius: 1,
              mb: 2,
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Typography color={CONFIG.colors.warningText}>
              {CONFIG.messages.moduleInactive}
            </Typography>
            {userRole === "admin" && (
            <Button
              variant="contained"
              color="warning"
              size="small"
              onClick={handleEnableModule}
              sx={{
                backgroundColor: colors.blueAccent[700],
                color: colors.primary[100],
                "&:hover": {
                  backgroundColor: colors.blueAccent[600],
                },
              }}
            >
              {CONFIG.messages.enableModule}
            </Button>
            )}
          </Box>
        )}

        <Box display="flex" flexDirection="column" gap={2}>
          {loading ? (
            <Skeleton variant="rounded" width="100%" height={120} />
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <StatCard
                  title="Total Pending"
                  value={stats.totalApprovals}
                  icon={<PendingActions />}
                  theme={theme}
                  colors={CONFIG.colors}
                  description="Across all approval levels"
                  additionalInfo={`Highest: ${stats.highestLevel.value} (${stats.highestLevel.name})`}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <StatCard
                  title="Primary Approvals"
                  value={stats.primaryCount}
                  icon={<CheckCircle />}
                  theme={theme}
                  colors={CONFIG.colors}
                  description={`${stats.primaryPercentage.toFixed(
                    1
                  )}% of total`}
                  color={CONFIG.colors.primary}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <StatCard
                  title="Final Approvals"
                  value={stats.finalCount}
                  icon={<AssignmentTurnedIn />}
                  theme={theme}
                  colors={CONFIG.colors}
                  description={`${stats.finalPercentage.toFixed(1)}% of total`}
                  color={CONFIG.colors.final}
                />
              </Grid>
            </Grid>
          )}
        </Box>

        <Box flex={1} minHeight={300} position="relative" mt={2}>
          {loading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="100%"
              bgcolor={theme.palette.action.hover}
              borderRadius={2}
            >
              <CircularProgress size={isMobile ? 40 : 50} />
            </Box>
          ) : viewMode === "pie" ? (
            <Box
              height="100%"
              bgcolor={theme.palette.action.hover}
              borderRadius={2}
              p={2}
              sx={{
                background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.action.hover} 100%)`,
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={chartData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={renderCustomizedLabel}
                    outerRadius={CONFIG.chart.outerRadius}
                    innerRadius={CONFIG.chart.innerRadius}
                    dataKey="value"
                    onMouseEnter={(_, index) => setHoveredIndex(index)}
                    onMouseLeave={() => setHoveredIndex(null)}
                    onClick={handlePieClick} // Added click handler for pie segments
                  >
                    {chartData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={getSegmentColor(index, hoveredIndex === index)}
                        style={{
                          transition: "all 0.3s ease",
                          cursor: "pointer",
                          opacity:
                            hoveredIndex !== null && hoveredIndex !== index
                              ? 0.5
                              : 1,
                        }}
                      />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend
                    layout={isMobile ? "horizontal" : "horizontal"}
                    verticalAlign="bottom"
                    align="center"
                    wrapperStyle={{
                      paddingTop: 10,
                    }}
                    formatter={(value) => (
                      <Typography
                        component="span"
                        variant="body2"
                        color={CONFIG.colors.textPrimary}
                        fontWeight={500}
                        sx={{ fontSize: { xs: "0.75rem", sm: "0.8125rem" } }}
                      >
                        {value}
                      </Typography>
                    )}
                  />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Approval Level</TableCell>
                    <TableCell align="right">Pending Count</TableCell>
                    <TableCell align="right">Percentage</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {chartData.map((item, index) => {
                    const percentage =
                      (item.value / stats.totalApprovals) * 100;
                    return (
                      <TableRow
                        key={index}
                        onClick={() => handleApprovalLevelClick(item.name)}
                        sx={{
                          cursor: "pointer",
                          "&:hover": {
                            backgroundColor: theme.palette.action.hover,
                          },
                          transition: "background-color 0.2s ease",
                        }}
                      >
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Box
                              width={12}
                              height={12}
                              bgcolor={getSegmentColor(index, false)}
                              borderRadius="2px"
                            />
                            {item.name}
                          </Box>
                        </TableCell>
                        <TableCell align="right">{item.value}</TableCell>
                        <TableCell align="right">
                          {stats.totalApprovals > 0
                            ? percentage.toFixed(1) + "%"
                            : "0%"}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {stats.totalApprovals > 0 && (
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {stats.totalApprovals}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        100%
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

const StatCard = ({
  title,
  value,
  description,
  additionalInfo,
  icon,
  theme,
  colors,
  color,
}) => {
  const cardColor = color || theme.palette.text.secondary;

  return (
    <Card
      variant="outlined"
      sx={{
        height: "100%",
        p: 2,
        borderRadius: 2,
        backgroundColor: colors.cardBg,
        borderColor: theme.palette.divider,
        position: "relative",
        overflow: "hidden",
        transition: "all 0.3s ease",
        "&:hover": {
          transform: "translateY(-2px)",
        },
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
      >
        <Typography variant="body2" color="text.secondary">
          {title}
        </Typography>
        {icon &&
          React.cloneElement(icon, {
            fontSize: "small",
            sx: {
              color: cardColor,
              opacity: 0.8,
            },
          })}
      </Box>

      <Typography
        variant="h5"
        fontWeight={700}
        mt={0.5}
        sx={{ color: cardColor }}
      >
        {value}
      </Typography>

      {description && (
        <Typography variant="caption" color="text.secondary" mt={0.5}>
          {description}
        </Typography>
      )}

      {additionalInfo && (
        <Typography
          variant="caption"
          display="block"
          mt={0.5}
          color={cardColor}
        >
          {additionalInfo}
        </Typography>
      )}
    </Card>
  );
};

export default ApprovalPieChart;
